import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';

import useAuth from '@/composables/useAuth';
import {
  FILE_UPLOAD_DATA_SOURCE_TYPE,
  GOOGLE_SHEETS_DATA_SOURCE_TYPE,
  HS3_DATA_SOURCE_TYPE,
  HUBSPOT_DATA_SOURCE_TYPE,
  getDataSourceLabel,
} from '@/constants/data_sources';
import { useDataSourcesStore } from '@/stores';

export default function usePreferredCrm() {
  const dataSourcesStore = useDataSourcesStore();

  const { dataSources, supportedDataSources } = storeToRefs(dataSourcesStore);
  const { currentOrg } = useAuth();
  onMounted(async () => {
    await dataSourcesStore.readySync;
    const crm =
      HUBSPOT_DATA_SOURCE_TYPE === currentOrg.value.preferred_crm
        ? HS3_DATA_SOURCE_TYPE
        : currentOrg.value.preferred_crm;
    preferredCrm.value = [...crmOptions.value, ...nonCrmOptions].find(
      (ds) => ds.value === crm,
    )?.value;
  });

  const crmOptions = computed(() => {
    const crms = dataSources.value
      .filter(
        (ds) =>
          ![
            FILE_UPLOAD_DATA_SOURCE_TYPE,
            HUBSPOT_DATA_SOURCE_TYPE,
            GOOGLE_SHEETS_DATA_SOURCE_TYPE,
          ].includes(ds.type),
      )
      .map((ds) => ({
        value: ds.type,
        label: friendlyNameFor(ds),
        svg: `${ds.type}Icon`,
      }));

    return sortPreferredCrms(crms);
  });

  const nonCrmOptions = [
    { svg: '', value: 'not_listed', label: "I don't see my CRM listed" },
    { svg: '', value: 'no_crm', label: "I don't use a CRM" },
  ];

  const crmDropdownContent = computed(() => {
    return [
      {
        heading: null,
        items: crmOptions.value,
      },
      {
        heading: null,
        items: nonCrmOptions,
      },
    ];
  });

  const preferredCrm = ref(null);
  const isUnsupported = computed(() => {
    return !supportedDataSources.value.some(
      (ds) => ds.type === preferredCrm.value,
    );
  });

  const preferredCrmName = computed(() => {
    const ds = dataSources.value.find((ds) => ds.type === preferredCrm.value);
    return ds ? friendlyNameFor(ds) : undefined;
  });

  function friendlyNameFor(ds) {
    return getDataSourceLabel(ds.type, ds.friendly_name);
  }

  const showCrmCallout = computed(
    () => preferredCrm.value && preferredCrm.value !== 'no_crm',
  );
  const crmCalloutTitle = computed(() => {
    if (preferredCrm.value === 'not_listed')
      return "We'd love to hear what CRM you use";
    if (isUnsupported.value)
      return `We don't support ${preferredCrmName.value ?? 'your CRM'} yet`;
    return `Great news, we support ${preferredCrmName.value}`;
  });
  const crmCalloutDescription = computed(() => {
    if (preferredCrm.value === 'not_listed')
      return 'If you want to cast a vote for your source of truth, please reach out at support@crossbeam.com';
    if (isUnsupported.value)
      return `We recommend Google Sheets for now but can let you know once a ${preferredCrmName.value} is available as a data source`;
    return 'You can use this connection for free to keep your data up-to-date';
  });

  const crmCalloutType = computed(() =>
    isUnsupported.value || preferredCrm.value === 'not_listed'
      ? 'info'
      : 'success',
  );

  function sortPreferredCrms(crmList) {
    const firstFiveCrms = [
      'hubspot_v3',
      'salesforce',
      'pipedrive',
      'microsoft_dynamics',
      'snowflake',
    ];
    return crmList.sort((a, b) => {
      const aIndex = firstFiveCrms.indexOf(a.value.toLowerCase());
      const bIndex = firstFiveCrms.indexOf(b.value.toLowerCase());
      // If both items are in the first five list, sort by their position in that list
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      // If only one item is in the first five list, it should come first
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      // For items not in the first five list, sort alphabetically
      return a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1;
    });
  }

  return {
    isUnsupported,
    crmDropdownContent,
    preferredCrm,
    preferredCrmName,
    showCrmCallout,
    crmCalloutTitle,
    crmCalloutDescription,
    crmCalloutType,
  };
}
